export interface IAudienceEstimate {
   matchedCount: number
   eligibility: {
      eligibleCount: number
      excludedCount: number
      temporarilyHeldCount: number
      exclusions: Record<string, number>
      temporaryHolds: Record<string, number>
   } | null
   scope?: 'campaign' | 'organization'
}

const isCount = (value: unknown): value is number =>
   typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

const counts = (value: unknown): Record<string, number> =>
   value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(
           Object.entries(value).filter(([, n]) => isCount(n) && n > 0)
        )
      : {}

/** A legacy raw count must never be presented as an eligibility result. */
export const parseAudienceEstimate = (value: unknown): IAudienceEstimate | null => {
   if (!value || typeof value !== 'object') return null
   const data = value as Record<string, unknown>
   const matched = data.matchedCount ?? data.count
   if (!isCount(matched)) return null
   const eligible = data.eligibleCount
   const excluded = data.excludedCount
   const held = data.temporarilyHeldCount
   return {
      matchedCount: matched,
      scope: data.scope === 'campaign' || data.scope === 'organization'
         ? data.scope : undefined,
      eligibility:
         isCount(eligible) && isCount(excluded) && isCount(held) &&
         eligible + excluded === matched && held <= eligible
            ? {
                 eligibleCount: eligible,
                 excludedCount: excluded,
                 temporarilyHeldCount: held,
                 exclusions: counts(data.exclusions),
                 temporaryHolds: counts(data.temporaryHolds),
              }
            : null,
   }
}

export interface IAudienceReason {
   key: string
   label: string
   count: number
   note?: string
}

const assignmentKeys = new Set(['already_enrolled'])

/** Partition the server's first-reason buckets; do not infer total membership. */
export const audienceAssignmentBreakdown = (estimate: IAudienceEstimate) => {
   const eligibility = estimate.eligibility
   if (!eligibility) return null
   const assignments = Object.fromEntries(
      Object.entries(eligibility.exclusions).filter(([key]) => assignmentKeys.has(key))
   )
   const alreadyAssigned = Object.values(assignments).reduce((sum, count) => sum + count, 0)
   const excludedTotal = Object.values(eligibility.exclusions).reduce((sum, count) => sum + count, 0)
   if (excludedTotal !== eligibility.excludedCount) return null
   return {
      alreadyAssigned,
      canAssign: eligibility.eligibleCount,
      cannotAssign: eligibility.excludedCount - alreadyAssigned,
      assignments,
      restrictions: Object.fromEntries(
         Object.entries(eligibility.exclusions).filter(([key]) => !assignmentKeys.has(key))
      ),
   }
}

const reasonLabels: Record<string, string> = {
   lead_data_unavailable: 'Lead information unavailable',
   do_not_contact: 'Do not contact',
   consent_unknown: 'Consent needs review',
   campaign_stop_rule: 'Campaign stop rules',
   organization_stop_rule: 'Organization stop rules (stages or tags)',
   already_enrolled: 'This campaign — current or past enrollment',
   missing_phone: 'Missing a required phone number',
   missing_email: 'Missing a required email address',
   ai_paused: 'AI is paused',
   transition_hold: 'Campaign transition in progress',
   active_conversation: 'Active conversation',
   work_in_progress: 'Other work in progress',
}

export const audienceReasons = (
   values: Record<string, number>,
   campaignScope: boolean
): IAudienceReason[] => {
   const rows = Object.entries(values)
      .filter(([key, count]) => count > 0 && !['other_campaign', 'assigned_elsewhere'].includes(key))
      .map(([key, count]) => ({
         key,
         count,
         label: reasonLabels[key] || 'Other eligibility restriction',
         note: key === 'already_enrolled'
            ? 'Current or past enrollment; these leads will not be enrolled again.'
            : undefined,
      }))
   const enrolled = values.other_campaign || 0
   const assigned = values.assigned_elsewhere || 0
   if (enrolled + assigned > 0) rows.push({
      key: 'campaign_assignment',
      label: campaignScope ? 'Assigned to another campaign' : 'Already assigned to a campaign',
      count: enrolled + assigned,
      note: undefined,
   })
   return rows
}
